/**
 * @file useItemSupplierOptions.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description The lists an item's Supplier is chosen from, and Search
 *              Inventory's Supplier filter (GET /api/inventory/supplier-options):
 *              { ready, reason, suppliers, groups, noneCount, groupsMap } —
 *              `groupsMap` is account id → group parent id
 *              (utils/itemSupplier.groupMapFrom), for the bill's code guess.
 *              Read once per mount; null fields until it answers. A failure
 *              reads as not ready, with why.
 */

import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import { groupMapFrom } from '../utils/itemSupplier';

export default function useItemSupplierOptions({ enabled = true } = {}) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/inventory/supplier-options`, { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!cancelled) setState(r.ok ? d : { ready: false, reason: d.error || 'Suppliers could not be read', suppliers: [], groups: [] });
      })
      .catch(() => { if (!cancelled) setState({ ready: false, reason: 'Suppliers could not be read', suppliers: [], groups: [] }); });
    return () => { cancelled = true; };
  }, [enabled]);
  const groupsMap = useMemo(() => groupMapFrom(state?.groups || []), [state]);
  return {
    loaded: !!state,
    ready: state ? state.ready !== false : null,
    reason: state?.reason || null,
    suppliers: state?.suppliers || [],
    groups: state?.groups || [],
    noneCount: state?.none_count || 0,
    groupsMap
  };
}
